import { ChangeDetectorRef, Component, EnvironmentInjector, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService } from '@services/api-client';
import { GetUserResponse, UserProfileResponse } from '@services/api-client/responses/get-user-response.type';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { CachedValuesService } from '@services/cached-values/cached-values.service';
import { ToastService } from '@services/toast/toast.service';
import {
    SubscriptionForm,
    UserForm
} from '../../components/create-and-update-user-modal/create-and-update-user-modal.model';
import { matchValidator, passwordValidator, RoutesHelper } from '@helpers/index';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { map, Observable, take } from 'rxjs';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { Customer } from 'app/types';
import { Role, ValidationMessageKey } from 'app/enums';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
    private readonly apiClientService = inject(ApiClientService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly injector = inject(EnvironmentInjector);
    private readonly cachedValuesService = inject(CachedValuesService);
    public showHeader = false;

    public showPotentialValidationMessages = true;
    public userForm: FormGroup<UserForm>;
    public subscriptionForm: FormGroup<SubscriptionForm>;
    public userProfile: UserProfileResponse = {} as UserProfileResponse;
    public editMode = false;
    public editPassword = false;
    public editSubscriptionMode = false;
    private originalUserFormValues: {
        firstName?: string;
        lastName?: string;
        email?: string;
        title?: string;
        phone?: string;
        customerId?: string;
        password?: string;
        inboxEmail?: string;
        confirmPassword?: string;
        roles?: Role[];
    } | null = null;

    private originalSubscriptionFormValues: {
        ai?: boolean | null;
        cancelled?: boolean | null;
        numOfUsers?: number | null;
        numOfReaders?: number | null;
    } | null = null;

    private numberOfActiveUsers: number = 0;

    public invoiceIntervalsOptions: DropdownItem<number>[] = this.cachedValuesService
        .getInvoiceIntervalsOptions()
        .filter((item) => item.value === 3 || item.value === 6); // Only show Monthly and Annually

    private minActiveUsersValidator(minUsers: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: number = control.value as number;
            if (value === null || value === undefined) {
                return null; // Let required validator handle empty values
            }

            if (value < minUsers) {
                return {
                    minActiveUsers: {
                        actualValue: value,
                        requiredValue: minUsers
                    }
                };
            }

            return null;
        };
    }

    public get f() {
        return this.userForm.controls;
    }

    public get fs() {
        return this.subscriptionForm.controls;
    }

    public readonly customers$: Observable<DropdownItem<string>[]> = this.cachedValuesService.customers$.pipe(
        map((data) =>
            data.map((item: Customer) => {
                return {
                    value: item.id,
                    label: item.name
                } as DropdownItem<string>;
            })
        )
    );

    public ngOnInit(): void {
        const fb = new FormBuilder();
        this.subscriptionForm = fb.nonNullable.group({
            ai: [true, [Validators.required]],
            riskAnalysis: [true, [Validators.required]],
            cancelled: [false, [Validators.required]],
            numOfUsers: [1, [Validators.required]],
            numOfReaders: [0],
            numOfCredits: [100],
            invoiceIntervalId: [3, [Validators.required]]
        });
        this.userForm = fb.nonNullable.group({
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.email]],
            title: [''],
            phone: [''],
            customerId: [''],
            password: [''],
            confirmPassword: [''],
            inboxEmail: [''],
            roles: [[] as Role[]]
        });
        const userId = this.authenticationService.getUser().userId;
        this.apiClientService.getUser$(userId).subscribe({
            next: (response: GetUserResponse) => {
                if (response) {
                    this.userForm = fb.nonNullable.group({
                        firstName: [response?.firstName, [Validators.required]],
                        lastName: [response?.lastName, [Validators.required]],
                        email: [response?.email, [Validators.required, Validators.email]],
                        title: [response?.title],
                        phone: [response?.phone],
                        customerId: [response?.customerId],
                        password: [''],
                        confirmPassword: [''],
                        inboxEmail: [response?.inboxEmail],
                        roles: [response?.roles ?? []]
                    });

                    const setPasswordValidators = () => {
                        const password = this.f.password.value;
                        const confirmPassword = this.f.confirmPassword.value;
                        const shouldValidate = password || confirmPassword;

                        if (shouldValidate) {
                            this.userForm.setValidators([
                                matchValidator('password', 'confirmPassword', ValidationMessageKey.PasswordsMismatch),
                                passwordValidator('password', ValidationMessageKey.WeakPassword)
                            ]);
                            this.f.password.setValidators([Validators.required]);
                            this.f.confirmPassword.setValidators([Validators.required]);
                        } else {
                            this.userForm.clearValidators();
                            this.f.password.clearValidators();
                            this.f.confirmPassword.clearValidators();
                        }
                        this.f.password.updateValueAndValidity({ emitEvent: false });
                        this.f.confirmPassword.updateValueAndValidity({ emitEvent: false });
                        this.userForm.updateValueAndValidity({ emitEvent: false });
                    };

                    // Listen for changes to password fields
                    this.f.password.valueChanges.subscribe(() => setPasswordValidators());
                    this.f.confirmPassword.valueChanges.subscribe(() => setPasswordValidators());

                    setPasswordValidators();
                }
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant('FEATURES.USER.TOAST_GET_ERROR.MESSAGE') as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            }
        });

        this.apiClientService.getUserProfile$(userId).subscribe({
            next: (response) => {
                this.userProfile = response;
                this.changeDetectorRef.markForCheck();
            }
        });

        this.apiClientService
            .getSubscription$()
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.numberOfActiveUsers = response?.activeUsers || 0;

                    this.subscriptionForm = fb.nonNullable.group({
                        ai: [response?.ai, [Validators.required]],
                        riskAnalysis: [response?.riskAnalysis, [Validators.required]],
                        cancelled: [response?.cancelled, [Validators.required]],
                        numOfUsers: [
                            response?.numOfUsers,
                            [Validators.required, this.minActiveUsersValidator(this.numberOfActiveUsers)]
                        ],
                        numOfReaders: [response?.numOfReaders],
                        numOfCredits: [response?.numOfCredits],
                        invoiceIntervalId: [response?.invoiceIntervalId, [Validators.required]]
                    });
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    public get calculatedSubscriptionCost(): number {
        const numOfUsers = this.fs.numOfUsers.value || 1;
        const ai = this.fs.ai.value || false;
        const invoiceIntervalId = this.fs.invoiceIntervalId.value;
        let cost = 799;

        if (ai) {
            cost += 0;
        }

        cost += (numOfUsers - 1) * 99;

        // Apply 10% discount for annual billing (invoiceIntervalId === 6)
        if (invoiceIntervalId === 6) {
            cost *= 0.9;
        }

        return Math.round(cost);
    }

    public get customerName$(): Observable<string> {
        return this.customers$.pipe(
            map((customers: DropdownItem<string>[]) => {
                const customer = customers.find(
                    (c: DropdownItem<string>) => c.value === this.userForm?.controls.customerId.value
                );
                return customer ? customer.label : '-';
            })
        );
    }

    public saveUser() {
        if (this.userForm.invalid) {
            this.showPotentialValidationMessages = true;
            this.changeDetectorRef.markForCheck();
            return;
        }

        this.apiClientService
            .updateUser$({
                userId: this.authenticationService.getUser().userId,
                firstName: this.f.firstName.value,
                lastName: this.f.lastName.value,
                email: this.f.email.value,
                title: this.f.title.value,
                phone: this.f.phone.value,
                customerId: this.f.customerId.value,
                setPassword: this.f.password.value,
                roles: this.f.roles.value
            })
            .pipe(take(1))
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.UPDATE_USER_SUCCESS') as string;
                    this.cachedValuesService.invalidateCache();
                    this.toastService.open(title, message, { type: 'success' });
                    this.editMode = false;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.changeDetectorRef.markForCheck();
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.UPDATE_USER_ERROR') as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public savePassword() {
        if (this.userForm.invalid) {
            this.showPotentialValidationMessages = true;
            this.changeDetectorRef.markForCheck();
            return;
        }
    }

    public saveSubscription() {
        if (this.subscriptionForm.invalid) {
            this.showPotentialValidationMessages = true;
            this.changeDetectorRef.markForCheck();
            return;
        }
        this.fs.numOfReaders.setValue(this.fs.numOfUsers.value * 5);
        const request = {
            ai: this.fs.ai.value,
            cancelled: this.fs.cancelled.value,
            numOfUsers: this.fs.numOfUsers.value,
            numOfReaders: this.fs.numOfReaders.value,
            riskAnalysis: this.fs.riskAnalysis.value,
            invoiceIntervalId: this.fs.invoiceIntervalId.value
        };
        this.apiClientService
            .updateSubscription(request)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'TOAST_MESSAGES.UPDATE_SUBSCRIPTION_SUCCESS'
                    ) as string;
                    this.cachedValuesService.invalidateCache();
                    this.toastService.open(title, message, { type: 'success' });
                    this.editSubscriptionMode = false;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.changeDetectorRef.markForCheck();
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.UPDATE_SUBSCRIPTION_ERROR') as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public logout() {
        this.injector.runInContext(() => {
            RoutesHelper.RunInContext.navigateToLoginAndTriggerLogout();
        });
    }

    public enterEditMode() {
        this.originalUserFormValues = { ...this.userForm.getRawValue() };
        this.editMode = true;
    }

    public cancelEditMode() {
        if (this.originalUserFormValues) {
            this.userForm.patchValue(this.originalUserFormValues);
            this.userForm.markAsPristine();
        }
        this.editMode = false;
    }

    public enterEditPassword() {
        this.originalUserFormValues = { ...this.userForm.getRawValue() };
        this.editPassword = true;
    }

    public cancelEditPassword() {
        if (this.originalUserFormValues) {
            this.userForm.patchValue(this.originalUserFormValues);
            this.userForm.markAsPristine();
        }
        this.editPassword = false;
    }

    public enterEditSubscriptionMode() {
        this.originalSubscriptionFormValues = { ...this.subscriptionForm.getRawValue() };
        this.editSubscriptionMode = true;
    }

    public cancelEditSubscriptionMode() {
        if (this.originalSubscriptionFormValues) {
            this.subscriptionForm.patchValue(this.originalSubscriptionFormValues);
            this.subscriptionForm.markAsPristine();
        }
        this.editSubscriptionMode = false;
    }
}
